import {calculatePeriodAccounting} from "../../utils/periodAccounting";
import {escapeHtml as escapePrintValue} from "../../utils/escapeHtml";
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";

import { db } from "../../firebase";

import {
  calculateBill,
  formatMoney,
  roundMoney,
  toAmount,
} from "../../services/billingService";

import {
  getBusinessDateKey,
} from "../../utils/businessDate";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const lower = (value) =>
  clean(value).toLowerCase();

const getDateObject = (value) => {
  if (!value) return null;

  if (typeof value?.toDate === "function") {
    return value.toDate();
  }

  if (value?.seconds) {
    return new Date(
      value.seconds * 1000
    );
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? null
    : date;
};

const getTimestamp = (value) =>
  getDateObject(value)?.getTime() || 0;

const formatDateTime = (value) => {
  const date = getDateObject(value);

  if (!date) {
    return clean(value) || "-";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizePaymentMode = (value) => {
  const mode = lower(value);

  if (mode === "cash") return "Cash";
  if (mode === "upi") return "UPI";
  if (mode === "card") return "Card";

  if (
    mode.includes("bank") ||
    mode.includes("transfer")
  ) {
    return "Bank Transfer";
  }

  if (mode.includes("fab")) {
    return "Fab";
  }

  if (mode.includes("brevi")) {
    return "Brevistay";
  }

  if (
    mode.includes("ota") ||
    mode.includes("booking") ||
    mode.includes("agoda") ||
    mode.includes("goibibo") ||
    mode.includes("makemytrip")
  ) {
    return "Other OTA";
  }

  return clean(value) || "Other";
};

const normalizeBookingSource = (value) => {
  const source = lower(value);

  if (!source) return "Walk-In";

  if (source.includes("walk")) {
    return "Walk-In";
  }

  if (source.includes("fab")) {
    return "Fab";
  }

  if (source.includes("brevi")) {
    return "Brevistay";
  }

  if (
    source.includes("booking.com") ||
    source === "booking"
  ) {
    return "Booking.com";
  }

  if (source.includes("agoda")) {
    return "Agoda";
  }

  if (
    source.includes("goibibo") ||
    source.includes("go ibibo")
  ) {
    return "Goibibo";
  }

  if (
    source.includes("makemytrip") ||
    source.includes("mmt")
  ) {
    return "MakeMyTrip";
  }

  if (source.includes("corporate")) {
    return "Corporate";
  }

  if (source.includes("direct")) {
    return "Direct";
  }

  return clean(value) || "Other";
};

const getStayBusinessDate = (stay = {}) =>
  clean(
    stay.businessDate ||
      stay.checkInBusinessDate ||
      stay.checkoutBusinessDate
  );

const getExpenseBusinessDate = (
  expense = {}
) =>
  clean(expense.businessDate);

const getPaymentEntries = (stay = {}) => {
  const payments = Array.isArray(
    stay.payments
  )
    ? [...stay.payments]
    : [];

  const advance = Math.max(
    0,
    toAmount(stay.advance)
  );

  const advanceAlreadyIncluded =
    payments.some(
      (payment) =>
        payment?.source ===
          "check_in_advance" ||
        payment?.source ===
          "check_in_source_prepaid" ||
        payment?.source ===
          "check_in_guest_payment" || payment?.source === "booking_advance_applied" || payment?.source === "check_in_prior_advance" ||
        payment?.note ===
          "Check-In Advance" ||
        payment?.title ===
          "Advance Received"
    );

  if (
    advance > 0 &&
    !advanceAlreadyIncluded
  ) {
    payments.unshift({
      amount: advance,

      paymentMode:
        stay.paymentMode || "Cash",

      transactionReference:
        stay.transactionReference ||
        "",

      title: "Advance Received",

      source: "check_in_advance",

      addedAt:
        stay.checkInAt ||
        stay.createdAtIso ||
        "",
    });
  }

  return payments;
};

const getPrimaryGuest = (stay = {}) => {
  if (
    Array.isArray(stay.guests) &&
    stay.guests.length
  ) {
    return (
      stay.guests.find(
        (guest) =>
          guest?.isPrimary === true
      ) || stay.guests[0]
    );
  }

  return {
    name:
      stay.guestName ||
      stay.name ||
      "Guest",

    phoneNumber:
      stay.phoneNumber ||
      stay.phone ||
      "",
  };
};

const safePercentage = (
  value,
  total
) => {
  const numerator =
    toAmount(value);

  const denominator =
    toAmount(total);

  if (denominator <= 0) {
    return 0;
  }

  return Math.round(
    (numerator / denominator) * 100
  );
};

/* -------------------------------------------------------------------------- */
/*                              Reports Panel                                 */
/* -------------------------------------------------------------------------- */

function ReportsPanel({
  hotelId,
  hotelName,

  totalRoomCount = 0,
}) {
  const todayBusinessDate =
    getBusinessDateKey(new Date());

  const [
    fromDate,
    setFromDate,
  ] = useState(todayBusinessDate);

  const [
    toDate,
    setToDate,
  ] = useState(todayBusinessDate);

  const [accountingEntries, setAccountingEntries] = useState([]);
  const [accountingError, setAccountingError] = useState("");
  const [stays, setStays] =
    useState([]);

  const [expenses, setExpenses] =
    useState([]);

  const [loadingStays, setLoadingStays] =
    useState(true);

  const [
    loadingExpenses,
    setLoadingExpenses,
  ] = useState(true);

  const [loadError, setLoadError] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [
    stayStatusFilter,
    setStayStatusFilter,
  ] = useState("all");

  const [
    bookingSourceFilter,
    setBookingSourceFilter,
  ] = useState("all");

  const [
    paymentModeFilter,
    setPaymentModeFilter,
  ] = useState("all");

  const [sortBy, setSortBy] =
    useState("recent");

  /* ------------------------------------------------------------------------ */
  /*                              Stay Listener                                */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!hotelId) {
      setStays([]);
      setLoadingStays(false);

      return undefined;
    }

    setLoadingStays(true);
    setLoadError("");

    const staysQuery = query(
      collection(db, "hotelStays"),

      where(
        "hotelId",
        "==",
        hotelId
      )
    );

    return onSnapshot(
      staysQuery,

      (snapshot) => {
        const list = snapshot.docs
          .map((stayDocument) => ({
            id: stayDocument.id,
            stayId: stayDocument.id,

            ...stayDocument.data(),
          }))
          .sort(
            (firstStay, secondStay) =>
              getTimestamp(
                secondStay.checkInAt
              ) -
              getTimestamp(
                firstStay.checkInAt
              )
          );

        setStays(list);
        setLoadingStays(false);
      },

      (error) => {
        console.error(
          "Reports stays load error:",
          error
        );

        setStays([]);
        setLoadingStays(false);

        setLoadError(
          error?.message ||
            "Stay report load nahi hui."
        );
      }
    );
  }, [hotelId]);

  /* ------------------------------------------------------------------------ */
  /*                            Expense Listener                               */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!hotelId) {
      setExpenses([]);
      setLoadingExpenses(false);

      return undefined;
    }

    setLoadingExpenses(true);

    const expenseQuery = query(
      collection(
        db,
        "hotelExpenses"
      ),

      where(
        "hotelId",
        "==",
        hotelId
      )
    );

    return onSnapshot(
      expenseQuery,

      (snapshot) => {
        setExpenses(
          snapshot.docs.map(
            (expenseDocument) => ({
              id:
                expenseDocument.id,

              ...expenseDocument.data(),
            })
          )
        );

        setLoadingExpenses(false);
      },

      (error) => {
        console.error(
          "Reports expense load error:",
          error
        );

        setExpenses([]);
        setLoadingExpenses(false);

        setLoadError(
          error?.message ||
            "Expense report load nahi hui."
        );
      }
    );
  }, [hotelId]);

  /* ------------------------------------------------------------------------ */
  /*                          Selected Date Records                            */
  /* ------------------------------------------------------------------------ */

  const rangeStart =
    fromDate <= toDate
      ? fromDate
      : toDate;

  const rangeEnd =
    fromDate <= toDate
      ? toDate
      : fromDate;

  useEffect(() => {
    if (!hotelId) return undefined;
    return onSnapshot(query(collection(db, 'dayBookEntries'), where('hotelId', '==', hotelId)), snapshot => {
      setAccountingEntries(snapshot.docs.map(item => ({id: item.id, ...item.data()}))); setAccountingError('');
    }, error => setAccountingError('Day Book accounting load failed: ' + error.message));
  }, [hotelId]);

  const selectedStays = useMemo(
    () =>
      stays.filter((stay) => {
        const stayDate =
          getStayBusinessDate(stay);

        return (
          !["orphan_closed", "cancelled", "canceled"].includes(lower(stay.status)) &&
          stayDate &&
          stayDate >= rangeStart &&
          stayDate <= rangeEnd
        );
      }),
    [stays, rangeStart, rangeEnd]
  );

  const selectedExpenses = useMemo(
    () =>
      expenses.filter((expense) => {
        const expenseDate =
          getExpenseBusinessDate(
            expense
          );

        return (
          expenseDate &&
          expenseDate >= rangeStart &&
          expenseDate <= rangeEnd
        );
      }),
    [expenses, rangeStart, rangeEnd]
  );

  /* ------------------------------------------------------------------------ */
  /*                               Totals                                      */
  /* ------------------------------------------------------------------------ */

  const report = useMemo(() => {
    const result = {
      totalStays:
        selectedStays.length,

      checkedIn: 0,

      checkedOut: 0,

      cancelled: 0,

      roomRevenue: 0,

      foodRevenue: 0,

      laundryRevenue: 0,

      extraRevenue: 0,

      grossRevenue: 0,

      discount: 0,

      gst: 0,

      grandRevenue: 0,

      totalPaid: 0,

      pendingBalance: 0,

      excessPaid: 0,

      totalExpenses: 0,

      cashExpense: 0,

      onlineExpense: 0,

      netRevenue: 0,

      occupiedRoomCount: 0,

      occupancyPercentage: 0,

      averageRoomRate: 0,

      averageBillValue: 0,

      paymentModes: {},

      bookingSources: {},

      fabBookings: 0,
      fabTotal: 0,

      brevistayBookings: 0,
      brevistayTotal: 0,

      roomRevenueByRoom: {},
    };

    selectedStays.forEach((stay) => {
      const status =
        lower(stay.status);

      if (
        status === "checked_out" ||
        stay.checkedOut === true
      ) {
        result.checkedOut += 1;
      } else if (
        status === "cancelled" ||
        status === "canceled"
      ) {
        result.cancelled += 1;
      } else {
        result.checkedIn += 1;
      }

      const bill =
        calculateBill(stay, {
          discountAmount:
            stay.discountAmount,

          gstRate:
            stay.gstRate,

          paid:
            stay.paidTotal ??
            stay.paid,
        });

      result.roomRevenue +=
        bill.roomRent;

      result.foodRevenue +=
        bill.food;

      result.laundryRevenue +=
        bill.laundry;

      result.extraRevenue +=
        bill.extra;

      result.grossRevenue +=
        bill.grossTotal;

      result.discount +=
        bill.discount;

      result.gst +=
        bill.gstAmount;

      result.grandRevenue +=
        bill.grandTotal;

      result.totalPaid +=
        bill.paid;

      result.pendingBalance +=
        bill.balance;

      result.excessPaid +=
        bill.excessPaid;

      const room = clean(
        stay.room
      ).toUpperCase();

      if (room) {
        result.roomRevenueByRoom[room] =
          roundMoney(
            toAmount(
              result.roomRevenueByRoom[
                room
              ]
            ) + bill.grandTotal
          );
      }

      const bookingSource =
        normalizeBookingSource(
          stay.bookingSource
        );

      result.bookingSources[
        bookingSource
      ] = roundMoney(
        toAmount(
          result.bookingSources[
            bookingSource
          ]
        ) + bill.grandTotal
      );

      if (bookingSource === "Fab") {
        result.fabBookings += 1;
        result.fabTotal +=
          bill.grandTotal;
      }

      if (
        bookingSource === "Brevistay"
      ) {
        result.brevistayBookings += 1;
        result.brevistayTotal +=
          bill.grandTotal;
      }

      getPaymentEntries(
        stay
      ).forEach((payment) => {
        const amount = Math.max(
          0,
          toAmount(payment.amount)
        );

        const paymentMode =
          normalizePaymentMode(
            payment.paymentMode
          );

        result.paymentModes[
          paymentMode
        ] = roundMoney(
          toAmount(
            result.paymentModes[
              paymentMode
            ]
          ) + amount
        );
      });
    });

    selectedExpenses.forEach(
      (expense) => {
        const amount = Math.max(
          0,
          toAmount(expense.amount)
        );

        result.totalExpenses +=
          amount;

        if (
          normalizePaymentMode(
            expense.paymentMode
          ) === "Cash"
        ) {
          result.cashExpense +=
            amount;
        } else {
          result.onlineExpense +=
            amount;
        }
      }
    );

    accountingEntries.filter(e=>e.category==='booking_advance_refund' && e.businessDate>=rangeStart && e.businessDate<=rangeEnd).forEach(e=>{
      const n=toAmount(e.amount);result.totalExpenses+=n;
      if(normalizePaymentMode(e.paymentMode)==='Cash')result.cashExpense+=n;else result.onlineExpense+=n;
    });
    Object.assign(result, calculatePeriodAccounting(accountingEntries, rangeStart, rangeEnd));
    const occupiedRooms = new Set(stays.filter(stay => {
      if (['orphan_closed', 'cancelled', 'canceled'].includes(lower(stay.status))) return false;
      const start = getStayBusinessDate(stay);
      const end = stay.checkoutBusinessDate || (stay.actualCheckOutAt ? getBusinessDateKey(new Date(stay.actualCheckOutAt)) : '9999-12-31');
      return start && start <= rangeEnd && end > rangeEnd;
    }).map(stay => clean(stay.room).toUpperCase()).filter(Boolean));

    result.occupiedRoomCount =
      occupiedRooms.size;

    result.occupancyPercentage =
      totalRoomCount > 0
        ? safePercentage(
            result.occupiedRoomCount,
            totalRoomCount
          )
        : 0;

    result.averageRoomRate =
      result.totalStays > 0
        ? roundMoney(
            result.roomRevenue /
              result.totalStays
          )
        : 0;

    result.averageBillValue =
      result.totalStays > 0
        ? roundMoney(
            result.grandRevenue /
              result.totalStays
          )
        : 0;

    result.netRevenue =
      roundMoney(
        result.grandRevenue -
          result.totalExpenses
      );

    Object.keys(result).forEach(
      (key) => {
        if (
          typeof result[key] ===
          "number"
        ) {
          result[key] =
            roundMoney(
              result[key]
            );
        }
      }
    );

    return result;
  }, [
    selectedStays,
    selectedExpenses, accountingEntries, stays, rangeStart, rangeEnd,
    totalRoomCount,
  ]);

  /* ------------------------------------------------------------------------ */
  /*                      Dropdown Filter Options                              */
  /* ------------------------------------------------------------------------ */

  const bookingSourceOptions =
    useMemo(
      () =>
        Array.from(
          new Set(
            selectedStays.map(
              (stay) =>
                normalizeBookingSource(
                  stay.bookingSource
                )
            )
          )
        ).sort(),
      [selectedStays]
    );

  const paymentModeOptions =
    useMemo(
      () =>
        Array.from(
          new Set(
            selectedStays.flatMap(
              (stay) =>
                getPaymentEntries(
                  stay
                ).map((payment) =>
                  normalizePaymentMode(
                    payment.paymentMode
                  )
                )
            )
          )
        ).sort(),
      [selectedStays]
    );

  /* ------------------------------------------------------------------------ */
  /*                          Filtered Stay List                               */
  /* ------------------------------------------------------------------------ */

  const filteredStays = useMemo(() => {
    const searchTerm =
      lower(search);

    const filtered =
      selectedStays.filter((stay) => {
        const primaryGuest =
          getPrimaryGuest(stay);

        const bill =
          calculateBill(stay, {
            discountAmount:
              stay.discountAmount,

            gstRate:
              stay.gstRate,

            paid:
              stay.paidTotal ??
              stay.paid,
          });

        const paymentModes =
          getPaymentEntries(stay).map(
            (payment) =>
              normalizePaymentMode(
                payment.paymentMode
              )
          );

        const stayText = [
          stay.room,

          primaryGuest.name,

          primaryGuest.phoneNumber,

          stay.adRegisterSerialNo,

          stay.bookingSource,

          stay.invoiceNumber,

          stay.status,

          stay.otaBookingId,

          stay.corporateReference,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        const status =
          lower(stay.status);

        const matchesSearch =
          !searchTerm ||
          stayText.includes(
            searchTerm
          );

        const matchesStatus =
          stayStatusFilter === "all" ||
          (stayStatusFilter ===
            "checked_in" &&
            ![
              "checked_out",
              "cancelled",
              "canceled",
            ].includes(status)) ||
          (stayStatusFilter ===
            "checked_out" &&
            (status ===
              "checked_out" ||
              stay.checkedOut ===
                true)) ||
          (stayStatusFilter ===
            "cancelled" &&
            [
              "cancelled",
              "canceled",
            ].includes(status)) ||
          (stayStatusFilter ===
            "pending" &&
            bill.balance > 0);

        const matchesBookingSource =
          bookingSourceFilter ===
            "all" ||
          normalizeBookingSource(
            stay.bookingSource
          ) === bookingSourceFilter;

        const matchesPaymentMode =
          paymentModeFilter ===
            "all" ||
          paymentModes.includes(
            paymentModeFilter
          );

        return (
          matchesSearch &&
          matchesStatus &&
          matchesBookingSource &&
          matchesPaymentMode
        );
      });

    return [...filtered].sort(
      (firstStay, secondStay) => {
        const firstBill =
          calculateBill(firstStay, {
            discountAmount:
              firstStay.discountAmount,

            gstRate:
              firstStay.gstRate,

            paid:
              firstStay.paidTotal ??
              firstStay.paid,
          });

        const secondBill =
          calculateBill(secondStay, {
            discountAmount:
              secondStay.discountAmount,

            gstRate:
              secondStay.gstRate,

            paid:
              secondStay.paidTotal ??
              secondStay.paid,
          });

        if (sortBy === "amount") {
          return (
            secondBill.grandTotal -
            firstBill.grandTotal
          );
        }

        if (sortBy === "pending") {
          return (
            secondBill.balance -
            firstBill.balance
          );
        }

        if (sortBy === "room") {
          return clean(
            firstStay.room
          ).localeCompare(
            clean(secondStay.room),
            undefined,
            {
              numeric: true,
            }
          );
        }

        if (sortBy === "guest") {
          return clean(
            getPrimaryGuest(
              firstStay
            ).name
          ).localeCompare(
            clean(
              getPrimaryGuest(
                secondStay
              ).name
            )
          );
        }

        return (
          getTimestamp(
            secondStay.checkInAt
          ) -
          getTimestamp(
            firstStay.checkInAt
          )
        );
      }
    );
  }, [
    selectedStays,
    search,
    stayStatusFilter,
    bookingSourceFilter,
    paymentModeFilter,
    sortBy,
  ]);

  /* ------------------------------------------------------------------------ */
  /*                                  Print                                    */
  /* ------------------------------------------------------------------------ */

  const printReport = () => {
    const printWindow = window.open(
      "",
      "_blank",
      "width=1400,height=950"
    );

    if (!printWindow) {
      alert(
        "Print popup block ho gaya. Browser me popups allow karo."
      );

      return;
    }

    const stayRows = filteredStays
      .map((stay, index) => {
        const primaryGuest =
          getPrimaryGuest(stay);

        const bill =
          calculateBill(stay, {
            discountAmount:
              stay.discountAmount,

            gstRate:
              stay.gstRate,

            paid:
              stay.paidTotal ??
              stay.paid,
          });

        return `
          <tr>
            <td>${index + 1}</td>

            <td>${escapePrintValue(clean(
              stay.room || "-"
            ))}</td>

            <td>
              ${escapePrintValue(clean(
                primaryGuest.name ||
                  "Guest"
              ))}
            </td>

            <td>
              ${escapePrintValue(clean(
                primaryGuest.phoneNumber ||
                  "-"
              ))}
            </td>

            <td>
              ${escapePrintValue(clean(
                normalizeBookingSource(
                  stay.bookingSource
                )
              ))}
            </td>

            <td>
              ${escapePrintValue(clean(
                formatDateTime(
                  stay.checkInAt
                )
              ))}
            </td>

            <td>
              ${escapePrintValue(clean(
                formatDateTime(
                  stay.actualCheckOutAt ||
                    stay.checkedOutAt
                )
              ))}
            </td>

            <td class="right">
              ${formatMoney(
                bill.roomRent
              )}
            </td>

            <td class="right">
              ${formatMoney(
                bill.food +
                  bill.laundry +
                  bill.extra
              )}
            </td>

            <td class="right">
              ${formatMoney(
                bill.grandTotal
              )}
            </td>

            <td class="right">
              ${formatMoney(
                bill.paid
              )}
            </td>

            <td class="right pending">
              ${formatMoney(
                bill.balance
              )}
            </td>
          </tr>
        `;
      })
      .join("");

    printWindow.document.open();

    printWindow.document.write(`
      <!DOCTYPE html>

      <html>
        <head>
          <meta charset="UTF-8" />

          <title>
            Hotel Report ${rangeStart} to ${rangeEnd}
          </title>

          <style>
            * {
              box-sizing: border-box;
            }

            body {
              margin: 0;
              padding: 8mm;

              color: #0f172a;

              font-family:
                Arial,
                sans-serif;
            }

            h1 {
              margin: 0;

              text-align: center;

              font-size: 21px;
            }

            .subtitle {
              margin-top: 5px;

              text-align: center;

              color: #475569;

              font-size: 10px;
            }

            .summary {
              display: grid;

              grid-template-columns:
                repeat(4,1fr);

              gap: 5px;

              margin: 12px 0;
            }

            .summary-box {
              padding: 7px;

              border:
                1px solid #94a3b8;

              text-align: center;

              font-size: 8px;
            }

            .summary-box strong {
              display: block;

              margin-top: 4px;

              font-size: 11px;
            }

            table {
              width: 100%;

              border-collapse:
                collapse;
            }

            th,
            td {
              padding: 5px;

              border:
                1px solid #94a3b8;

              font-size: 7px;

              vertical-align: top;
            }

            th {
              background: #e2e8f0;

              text-align: left;
            }

            .right {
              text-align: right;
            }

            .pending {
              color: #b91c1c;

              font-weight: 900;
            }

            @page {
              size: A4 landscape;

              margin: 6mm;
            }
          </style>
        </head>

        <body>
          <h1>
            ${escapePrintValue(clean(
              hotelName || hotelId
            ))} — HOTEL REPORT
          </h1>

          <div class="subtitle">
            Date Range:
            ${rangeStart} to ${rangeEnd}
          </div>

          <div class="summary">
            <div class="summary-box">
              Total Stays
              <strong>
                ${report.totalStays}
              </strong>
            </div>

            <div class="summary-box">
              FAB Bookings
              <strong>
                ${report.fabBookings}
              </strong>
              ${formatMoney(report.fabTotal)}
            </div>

            <div class="summary-box">
              Brevistay Bookings
              <strong>
                ${report.brevistayBookings}
              </strong>
              ${formatMoney(report.brevistayTotal)}
            </div>

            <div class="summary-box">
              Grand Revenue
              <strong>
                ${formatMoney(
                  report.grandRevenue
                )}
              </strong>
            </div>

            <div class="summary-box">
              Total Paid
              <strong>
                ${formatMoney(
                  report.totalPaid
                )}
              </strong>
            </div>

            <div class="summary-box">
              Pending
              <strong>
                ${formatMoney(
                  report.pendingBalance
                )}
              </strong>
            </div>

            <div class="summary-box">
              Expenses
              <strong>
                ${formatMoney(
                  report.totalExpenses
                )}
              </strong>
            </div>

            <div class="summary-box">
              Net Revenue
              <strong>
                ${formatMoney(
                  report.netRevenue
                )}
              </strong>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Room</th>
                <th>Guest</th>
                <th>Phone</th>
                <th>Source</th>
                <th>Check-In</th>
                <th>Checkout</th>
                <th>Room Rent</th>
                <th>Other Charges</th>
                <th>Total</th>
                <th>Paid</th>
                <th>Pending</th>
              </tr>
            </thead>

            <tbody>
              ${stayRows}
            </tbody>
          </table>

          <script>
            window.onload = function () {
              setTimeout(function () {
                window.focus();
                window.print();
              }, 300);
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  const loading =
    loadingStays ||
    loadingExpenses;

  /* ------------------------------------------------------------------------ */
  /*                                  Render                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <section style={sectionStyle}>
      <p style={{fontSize: 12}}>Money totals: selected period mein posted sales/receipts. Stay counts: selected period ke arrivals. Occupancy: period ke end par active rooms.</p>
      {accountingError && <p role="alert">{accountingError}</p>}

      <header style={headerStyle}>
        <div>
          <h3 style={titleStyle}>
            📊 Hotel Reports
          </h3>

          <div style={subtitleStyle}>
            Revenue, occupancy, payments,
            expenses aur pending balance
          </div>
        </div>

        <div style={headerActionsStyle}>
          <label style={dateFieldStyle}>
            <span style={dateLabelStyle}>
              From Date
            </span>

            <input
              type="date"
              value={fromDate}
              onChange={(event) =>
                setFromDate(
                  event.target.value
                )
              }
              style={inputStyle}
            />
          </label>

          <label style={dateFieldStyle}>
            <span style={dateLabelStyle}>
              To Date
            </span>

            <input
              type="date"
              value={toDate}
              onChange={(event) =>
                setToDate(
                  event.target.value
                )
              }
              style={inputStyle}
            />
          </label>

          <button
            type="button"
            onClick={printReport}
            style={printButtonStyle}
          >
            🖨 Print Report
          </button>
        </div>
      </header>

      <div style={summaryGridStyle}>
        <SummaryCard
          label="Total Stays"
          value={report.totalStays}
        />

        <SummaryCard
          label="FAB Bookings"
          value={report.fabBookings}
        />

        <SummaryCard
          label="FAB Total"
          value={formatMoney(
            report.fabTotal
          )}
        />

        <SummaryCard
          label="Brevistay Bookings"
          value={report.brevistayBookings}
        />

        <SummaryCard
          label="Brevistay Total"
          value={formatMoney(
            report.brevistayTotal
          )}
        />

        <SummaryCard
          label="Checked-In"
          value={report.checkedIn}
        />

        <SummaryCard
          label="Checked-Out"
          value={report.checkedOut}
        />

        <SummaryCard
          label="Occupancy"
          value={`${report.occupancyPercentage}%`}
        />

        <SummaryCard
          label="Room Revenue"
          value={formatMoney(
            report.roomRevenue
          )}
        />

        <SummaryCard
          label="Grand Revenue"
          value={formatMoney(
            report.grandRevenue
          )}
        />

        <SummaryCard
          label="Total Paid"
          value={formatMoney(
            report.totalPaid
          )}
        />

        <SummaryCard
          label="Pending"
          value={formatMoney(
            report.pendingBalance
          )}
          danger={
            report.pendingBalance > 0
          }
        />

        <SummaryCard
          label="Expenses"
          value={formatMoney(
            report.totalExpenses
          )}
          danger={
            report.totalExpenses > 0
          }
        />

        <SummaryCard
          label="Net Revenue"
          value={formatMoney(
            report.netRevenue
          )}
          strong
          danger={
            report.netRevenue < 0
          }
        />

        <SummaryCard
          label="Average Room Rate"
          value={formatMoney(
            report.averageRoomRate
          )}
        />

        <SummaryCard
          label="Average Bill"
          value={formatMoney(
            report.averageBillValue
          )}
        />
      </div>

      <div style={detailsGridStyle}>
        <ReportBreakdown
          title="Revenue Breakdown"
          items={[
            [
              "Room Revenue",
              report.roomRevenue,
            ],

            [
              "Food Revenue",
              report.foodRevenue,
            ],

            [
              "Laundry Revenue",
              report.laundryRevenue,
            ],

            [
              "Extra Revenue",
              report.extraRevenue,
            ],

            [
              "Discount",
              -report.discount,
            ],

            [
              "GST",
              report.gst,
            ],
          ]}
        />

        <ReportBreakdown
          title="Expense Breakdown"
          items={[
            [
              "Total Expenses",
              report.totalExpenses,
            ],

            [
              "Cash Expenses",
              report.cashExpense,
            ],

            [
              "Online Expenses",
              report.onlineExpense,
            ],

            [
              "Net Revenue",
              report.netRevenue,
            ],
          ]}
        />

        <BookingSourceSummary
          fabBookings={report.fabBookings}
          fabTotal={report.fabTotal}
          brevistayBookings={
            report.brevistayBookings
          }
          brevistayTotal={
            report.brevistayTotal
          }
        />

        <ObjectBreakdown
          title="Payment Modes"
          data={report.paymentModes}
        />

        <ObjectBreakdown
          title="Booking Sources"
          data={report.bookingSources}
        />
      </div>

      <div style={toolbarStyle}>
        <input
          value={search}
          onChange={(event) =>
            setSearch(
              event.target.value
            )
          }
          placeholder="Room, guest, phone, invoice, A&D serial..."
          style={inputStyle}
        />

        <select
          value={stayStatusFilter}
          onChange={(event) =>
            setStayStatusFilter(
              event.target.value
            )
          }
          style={inputStyle}
        >
          <option value="all">
            All Stays
          </option>

          <option value="checked_in">
            Checked-In
          </option>

          <option value="checked_out">
            Checked-Out
          </option>

          <option value="pending">
            Pending Balance
          </option>

          <option value="cancelled">
            Cancelled
          </option>
        </select>

        <select
          value={
            bookingSourceFilter
          }
          onChange={(event) =>
            setBookingSourceFilter(
              event.target.value
            )
          }
          style={inputStyle}
        >
          <option value="all">
            All Booking Sources
          </option>

          {bookingSourceOptions.map(
            (source) => (
              <option
                key={source}
                value={source}
              >
                {source}
              </option>
            )
          )}
        </select>

        <select
          value={paymentModeFilter}
          onChange={(event) =>
            setPaymentModeFilter(
              event.target.value
            )
          }
          style={inputStyle}
        >
          <option value="all">
            All Payment Modes
          </option>

          {paymentModeOptions.map(
            (mode) => (
              <option
                key={mode}
                value={mode}
              >
                {mode}
              </option>
            )
          )}
        </select>

        <select
          value={sortBy}
          onChange={(event) =>
            setSortBy(
              event.target.value
            )
          }
          style={inputStyle}
        >
          <option value="recent">
            Recent First
          </option>

          <option value="amount">
            Highest Bill
          </option>

          <option value="pending">
            Highest Pending
          </option>

          <option value="room">
            Room Number
          </option>

          <option value="guest">
            Guest Name
          </option>
        </select>
      </div>

      {loadError && (
        <div style={errorBoxStyle}>
          {loadError}
        </div>
      )}

      {loading ? (
        <div style={emptyBoxStyle}>
          Report loading...
        </div>
      ) : filteredStays.length === 0 ? (
        <div style={emptyBoxStyle}>
          Selected date range ya filter ke liye
          stay record nahi mila.
        </div>
      ) : (
        <div style={tableWrapperStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>
                  Room
                </th>

                <th style={thStyle}>
                  Guest
                </th>

                <th style={thStyle}>
                  Booking Source
                </th>

                <th style={thStyle}>
                  Check-In
                </th>

                <th style={thStyle}>
                  Checkout
                </th>

                <th style={thStyle}>
                  Status
                </th>

                <th style={thRightStyle}>
                  Total
                </th>

                <th style={thRightStyle}>
                  Paid
                </th>

                <th style={thRightStyle}>
                  Pending
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredStays.map(
                (stay) => {
                  const primaryGuest =
                    getPrimaryGuest(stay);

                  const bill =
                    calculateBill(stay, {
                      discountAmount:
                        stay.discountAmount,

                      gstRate:
                        stay.gstRate,

                      paid:
                        stay.paidTotal ??
                        stay.paid,
                    });

                  return (
                    <tr key={stay.id}>
                      <td style={tdStyle}>
                        <strong>
                          {stay.room ||
                            "-"}
                        </strong>
                      </td>

                      <td style={tdStyle}>
                        <strong>
                          {primaryGuest.name ||
                            "Guest"}
                        </strong>

                        <div
                          style={subTextStyle}
                        >
                          {primaryGuest.phoneNumber ||
                            "-"}
                        </div>
                      </td>

                      <td style={tdStyle}>
                        <SourceBadge
                          source={normalizeBookingSource(
                            stay.bookingSource
                          )}
                        />
                      </td>

                      <td style={tdStyle}>
                        {formatDateTime(
                          stay.checkInAt
                        )}
                      </td>

                      <td style={tdStyle}>
                        {formatDateTime(
                          stay.actualCheckOutAt ||
                            stay.checkedOutAt
                        )}
                      </td>

                      <td style={tdStyle}>
                        <StatusBadge
                          stay={stay}
                        />
                      </td>

                      <td style={tdRightStyle}>
                        <strong>
                          {formatMoney(
                            bill.grandTotal
                          )}
                        </strong>
                      </td>

                      <td style={tdRightStyle}>
                        {formatMoney(
                          bill.paid
                        )}
                      </td>

                      <td
                        style={{
                          ...tdRightStyle,

                          color:
                            bill.balance > 0
                              ? "#dc2626"
                              : "#15803d",

                          fontWeight: 900,
                        }}
                      >
                        {formatMoney(
                          bill.balance
                        )}
                      </td>
                    </tr>
                  );
                }
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*                             Small Components                               */
/* -------------------------------------------------------------------------- */

function SummaryCard({
  label,
  value,
  strong = false,
  danger = false,
}) {
  return (
    <div style={summaryCardStyle}>
      <span style={summaryLabelStyle}>
        {label}
      </span>

      <strong
        style={{
          ...summaryValueStyle,

          fontSize: strong
            ? 20
            : 17,

          color: danger
            ? "#dc2626"
            : "#0f172a",
        }}
      >
        {value}
      </strong>
    </div>
  );
}

function ReportBreakdown({
  title,
  items,
}) {
  return (
    <div style={breakdownCardStyle}>
      <h4 style={breakdownTitleStyle}>
        {title}
      </h4>

      {items.map(
        ([label, value]) => (
          <div
            key={label}
            style={breakdownRowStyle}
          >
            <span>{label}</span>

            <strong>
              {formatMoney(value)}
            </strong>
          </div>
        )
      )}
    </div>
  );
}

function BookingSourceSummary({
  fabBookings,
  fabTotal,
  brevistayBookings,
  brevistayTotal,
}) {
  return (
    <div style={breakdownCardStyle}>
      <h4 style={breakdownTitleStyle}>
        FAB & Brevistay Report
      </h4>

      <div style={sourceSummaryRowStyle}>
        <div>
          <strong>FAB</strong>
          <div style={noDataTextStyle}>
            {fabBookings} Booking(s)
          </div>
        </div>

        <strong>
          {formatMoney(fabTotal)}
        </strong>
      </div>

      <div style={sourceSummaryRowStyle}>
        <div>
          <strong>Brevistay</strong>
          <div style={noDataTextStyle}>
            {brevistayBookings} Booking(s)
          </div>
        </div>

        <strong>
          {formatMoney(brevistayTotal)}
        </strong>
      </div>

      <div style={sourceSummaryTotalStyle}>
        <div>
          <strong>Combined</strong>
          <div style={noDataTextStyle}>
            {toAmount(fabBookings) +
              toAmount(brevistayBookings)} Booking(s)
          </div>
        </div>

        <strong>
          {formatMoney(
            toAmount(fabTotal) +
              toAmount(brevistayTotal)
          )}
        </strong>
      </div>
    </div>
  );
}

function ObjectBreakdown({
  title,
  data,
}) {
  const entries = Object.entries(
    data || {}
  ).sort(
    (
      [, firstAmount],
      [, secondAmount]
    ) =>
      toAmount(secondAmount) -
      toAmount(firstAmount)
  );

  return (
    <div style={breakdownCardStyle}>

      <h4 style={breakdownTitleStyle}>
        {title}
      </h4>

      {entries.length === 0 ? (
        <div style={noDataTextStyle}>
          No records
        </div>
      ) : (
        entries.map(
          ([label, value]) => (
            <div
              key={label}
              style={breakdownRowStyle}
            >
              <span>{label}</span>

              <strong>
                {formatMoney(value)}
              </strong>
            </div>
          )
        )
      )}
    </div>
  );
}

function SourceBadge({
  source,
}) {
  return (
    <span style={sourceBadgeStyle}>
      {source}
    </span>
  );
}

function StatusBadge({
  stay,
}) {
  const status =
    lower(stay?.status);

  if (
    status === "checked_out" ||
    stay?.checkedOut === true
  ) {
    return (
      <span
        style={{
          ...statusBadgeStyle,

          background: "#dbeafe",
          color: "#1d4ed8",
        }}
      >
        Checked-Out
      </span>
    );
  }

  if (
    status === "cancelled" ||
    status === "canceled"
  ) {
    return (
      <span
        style={{
          ...statusBadgeStyle,

          background: "#e2e8f0",
          color: "#475569",
        }}
      >
        Cancelled
      </span>
    );
  }

  return (
    <span
      style={{
        ...statusBadgeStyle,

        background: "#dcfce7",
        color: "#15803d",
      }}
    >
      Checked-In
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const sectionStyle = {
  marginTop: 34,
  padding: 18,

  border:
    "1px solid #e2e8f0",

  borderRadius: 18,

  background: "#ffffff",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",

  gap: 12,
  flexWrap: "wrap",
};

const titleStyle = {
  margin: 0,
  color: "#0f172a",
};

const subtitleStyle = {
  marginTop: 5,

  color: "#64748b",

  fontSize: 11,
};

const headerActionsStyle = {
  display: "flex",
  alignItems: "center",

  gap: 8,
  flexWrap: "wrap",
};

const dateFieldStyle = {
  display: "grid",
  gap: 4,
};

const dateLabelStyle = {
  color: "#64748b",
  fontSize: 9,
  fontWeight: 900,
  textTransform: "uppercase",
};

const summaryGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(130px,1fr))",

  gap: 9,

  marginTop: 15,
};

const summaryCardStyle = {
  padding: 11,

  border:
    "1px solid #e2e8f0",

  borderRadius: 11,

  background: "#f8fafc",
};

const summaryLabelStyle = {
  display: "block",

  color: "#64748b",

  fontSize: 9,
  fontWeight: 800,

  textTransform: "uppercase",
};

const summaryValueStyle = {
  display: "block",

  marginTop: 4,

  fontWeight: 900,

  overflowWrap: "anywhere",
};

const detailsGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(230px,1fr))",

  gap: 10,

  marginTop: 14,
};

const breakdownCardStyle = {
  padding: 12,

  border:
    "1px solid #e2e8f0",

  borderRadius: 12,

  background: "#f8fafc",
};

const breakdownTitleStyle = {
  margin: "0 0 9px",

  color: "#0f172a",

  fontSize: 13,
};

const breakdownRowStyle = {
  display: "flex",
  justifyContent: "space-between",

  gap: 10,

  padding: "5px 0",

  color: "#475569",

  fontSize: 11,

  borderBottom:
    "1px dashed #e2e8f0",
};

const sourceSummaryRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  padding: "8px 0",
  color: "#334155",
  fontSize: 11,
  borderBottom: "1px dashed #e2e8f0",
};

const sourceSummaryTotalStyle = {
  ...sourceSummaryRowStyle,
  marginTop: 3,
  paddingTop: 10,
  borderBottom: 0,
  color: "#0f172a",
};

const noDataTextStyle = {
  color: "#64748b",

  fontSize: 10,
};

const toolbarStyle = {
  display: "grid",

  gridTemplateColumns:
    "minmax(230px,1fr) repeat(4,minmax(135px,185px))",

  gap: 8,

  marginTop: 15,
};

const inputStyle = {
  width: "100%",
  minHeight: 42,

  padding: 10,

  border:
    "1px solid #cbd5e1",

  borderRadius: 9,

  background: "#ffffff",
  color: "#0f172a",

  boxSizing: "border-box",

  outline: "none",
};

const printButtonStyle = {
  minHeight: 42,

  padding: "9px 13px",

  border: 0,
  borderRadius: 9,

  background: "#0f766e",
  color: "#ffffff",

  fontWeight: 900,
  cursor: "pointer",
};

const errorBoxStyle = {
  marginTop: 12,
  padding: 11,

  border:
    "1px solid #fecaca",

  borderRadius: 10,

  background: "#fef2f2",
  color: "#b91c1c",

  fontSize: 11,
  fontWeight: 700,
};

const emptyBoxStyle = {
  marginTop: 12,
  padding: 17,

  border:
    "1px dashed #cbd5e1",

  borderRadius: 10,

  color: "#64748b",

  textAlign: "center",
};

const tableWrapperStyle = {
  marginTop: 12,

  overflowX: "auto",

  border:
    "1px solid #cbd5e1",

  borderRadius: 10,
};

const tableStyle = {
  width: "100%",

  minWidth: 1050,

  borderCollapse: "collapse",

  background: "#ffffff",
};

const thStyle = {
  padding: 9,

  borderBottom:
    "1px solid #cbd5e1",

  background: "#e2e8f0",

  color: "#334155",

  fontSize: 9,
  fontWeight: 900,

  textAlign: "left",

  whiteSpace: "nowrap",
};

const thRightStyle = {
  ...thStyle,

  textAlign: "right",
};

const tdStyle = {
  padding: 9,

  borderBottom:
    "1px solid #e2e8f0",

  color: "#334155",

  fontSize: 10,

  verticalAlign: "top",
};

const tdRightStyle = {
  ...tdStyle,

  textAlign: "right",
};

const subTextStyle = {
  marginTop: 3,

  color: "#64748b",

  fontSize: 8,
};

const sourceBadgeStyle = {
  display: "inline-block",

  padding: "4px 7px",

  borderRadius: 999,

  background: "#f3e8ff",
  color: "#7c3aed",

  fontSize: 9,
  fontWeight: 900,

  whiteSpace: "nowrap",
};

const statusBadgeStyle = {
  display: "inline-block",

  padding: "4px 7px",

  borderRadius: 999,

  fontSize: 9,
  fontWeight: 900,

  whiteSpace: "nowrap",
};

export default ReportsPanel;